import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import { AuthService } from '../services/auth';
import {
  type AvatarAssetCacheInput,
  AvatarAssetCacheService,
  getAvatarAssetCacheKey,
  toAbsoluteAvatarAssetUrl,
} from '../services/avatar-asset-cache';
import { ServerService } from '../services/server';

export type CachedUserAvatarInput = {
  userId?: string | null;
  avatarUrl?: string | null;
  scopeAccountId?: string | null;
};

export function useCachedUserAvatarUrl({
  userId,
  avatarUrl,
  scopeAccountId,
}: CachedUserAvatarInput): string | null | undefined {
  const avatarCache = useService(AvatarAssetCacheService);
  const server = useService(ServerService).server;
  const authService = useService(AuthService);
  const currentAccount = useLiveData(authService.session.account$);
  const trimmedAvatarUrl = avatarUrl?.trim() || null;
  const accountId = scopeAccountId ?? currentAccount?.id ?? null;
  const sourceUrl = useMemo(() => {
    return trimmedAvatarUrl
      ? toAbsoluteAvatarAssetUrl(trimmedAvatarUrl, server.baseUrl)
      : null;
  }, [server.baseUrl, trimmedAvatarUrl]);
  const input = useMemo<AvatarAssetCacheInput | null>(() => {
    if (!sourceUrl || !accountId) {
      return null;
    }
    const entityId = userId ?? sourceUrl;

    return {
      kind: 'user-avatar',
      scope: {
        accountId,
        serverId: server.id,
      },
      entityId,
      source: sourceUrl,
    };
  }, [accountId, server.id, sourceUrl, userId]);
  const key = input ? getAvatarAssetCacheKey(input) : null;
  const [resolved, setResolved] = useState<{
    key: string;
    url: string | null;
  } | null>(null);
  // A picture shown earlier in this session is there on the first frame.
  const shown = input ? avatarCache.peekDisplayUrl(input) : null;

  useEffect(() => {
    if (!input || !key || avatarCache.peekDisplayUrl(input)) {
      return;
    }
    let disposed = false;
    avatarCache
      .displayUrl(input, server.baseUrl)
      .then(url => {
        if (!disposed) setResolved({ key, url });
      })
      .catch(() => {
        // Avatar caching is cosmetic; failures keep the live URL/fallback path.
      });
    return () => {
      disposed = true;
    };
  }, [avatarCache, input, key, server.baseUrl]);

  const cachedUrl = shown ?? (resolved?.key === key ? resolved.url : null);
  return cachedUrl ?? trimmedAvatarUrl ?? avatarUrl;
}
